import type { PetitionDetailsPageData } from 'src/app/pages/petitionDetails/pageContext';

import type { NormalizedDecisionMaker } from '../../../../shared/types';
import { MAX_DISPLAYED_DM_CARDS_ON_PETITION } from '../../../../shared/types';
import type { ProcessedDecisionMakerData } from '../../types';

import {
	formatAllBuckets,
	formatForDMsAndLBs,
	getLegislativeBodies,
	getPrimaryDms,
	normalizeDms,
	sortAllBuildingBlocks,
	sortAllDms,
} from './utils';

export type PrimaryDecisionMakerConnection = PetitionDetailsPageData['petition']['primaryDecisionMakerConnection'];
export type AllDMResponses = PetitionDetailsPageData['petition']['dmResponsesConnection']['nodes'];

export function processDMConnectionData(
	primaryDecisionMakerConnection: PrimaryDecisionMakerConnection,
	allDmResponses: AllDMResponses,
): ProcessedDecisionMakerData {
	const { nodes: dms, totalCount: totalDmCount } = primaryDecisionMakerConnection;

	const normalizedAll = normalizeDms(dms, allDmResponses);
	const politiciansOnly = normalizedAll.filter((dm: NormalizedDecisionMaker) => dm.type === 'POLITICIAN');

	// One bucket: primary DMs
	const primaryDMs = getPrimaryDms(politiciansOnly);

	// One bucket: legislative bodies
	const primaryDmIds = new Set(primaryDMs.map((dm) => dm.id));
	const politiciansExcludingPrimaries = politiciansOnly.filter((dm) => !primaryDmIds.has(dm.id));
	const legislativeBodiesList = getLegislativeBodies(politiciansExcludingPrimaries);

	const legislativeBodiesWithDmsSorted = legislativeBodiesList.map((body) => {
		const sortedLBDMs = sortAllDms(body.groupDms);

		const hasMissing = sortedLBDMs.some((dm) => !dm.location);
		const uniqueLocations = [...new Set(sortedLBDMs.map((dm) => dm.location))];
		const location = !hasMissing && uniqueLocations.length === 1 ? uniqueLocations[0] : null;

		return {
			title: body.groupTitle,
			location,
			responseCount: sortedLBDMs.filter((x) => x.response).length,
			decisionMakers: sortedLBDMs,
		};
	});
	const legislativeBodyDmIds = new Set(
		legislativeBodiesWithDmsSorted.flatMap((body) => body.decisionMakers.map((dm) => dm.id)),
	);

	// One bucket: politicians that are neither primary nor in a legislative body
	const individualPoliticians = politiciansOnly.filter((dm) => {
		return !legislativeBodyDmIds.has(dm.id) && !primaryDmIds.has(dm.id);
	});

	// One bucket: remaining DMs
	const remainingDMs = normalizedAll.filter((dm) => dm.type !== 'POLITICIAN');

	const allFormattedBucketsOfDms = formatAllBuckets(
		primaryDMs,
		legislativeBodiesWithDmsSorted,
		individualPoliticians,
		remainingDMs,
	);
	const sortedAllDms = sortAllBuildingBlocks(allFormattedBucketsOfDms);

	const dmsAndLBs = formatForDMsAndLBs(sortedAllDms);

	return {
		dmsAndLBs: dmsAndLBs.slice(0, MAX_DISPLAYED_DM_CARDS_ON_PETITION),
		totalDmCount,
		displayViewAllButton: sortedAllDms.length > MAX_DISPLAYED_DM_CARDS_ON_PETITION,
	};
}
